import { z } from 'zod';

/**
 * Phase 19 v1.4 — PIX-01/02/03 shared schemas.
 *
 * Crosses backend (PagamentosController DTOs via nestjs-zod) and frontend
 * (Angular reactive forms + ApiClient response validation). One source of
 * truth; both sides import the same Zod object.
 *
 * BigInt-cents convention: amounts are serialized as strings over the wire
 * (BigInt is not JSON-native). Backend converts to BigInt on read; frontend
 * displays via CurrencyPipe after `Number(str)` (safe up to R$ 90 trillion
 * — beyond MAX_SAFE_INTEGER concerns for any realistic event budget).
 */

// ---------- Enums (mirror Prisma) ----------

export const PagamentoStatusSchema = z.enum([
  'pendente',
  'pago',
  'expirado',
]);

export const TipoChavePixSchema = z.enum([
  'cpf',
  'cnpj',
  'email',
  'celular',
  'aleatoria',
]);

export const WithdrawalStatusSchema = z.enum([
  'pendente',
  'concluido',
  'falhou',
]);

// ---------- PIX charge (PIX-01) ----------

/**
 * Response from POST /pagamentos/evento/:eventoId/pix-charge and from
 * GET /pagamentos/evento/:eventoId/status. Frontend polls the GET every
 * 5s while `status === 'pendente'` and stops on `pago` or `expirado`.
 */
export const PagamentoEventoDtoSchema = z.object({
  id: z.string(),
  eventoId: z.string(),
  chargeId: z.string(),
  status: PagamentoStatusSchema,
  qrCode: z.string(),
  qrCodeBase64: z.string().nullable(),
  valorCents: z.string(), // BigInt serialized as decimal string
  expiraEm: z.string().nullable(),
  pagoEm: z.string().nullable(),
  criadoEm: z.string(),
});

// ---------- PIX withdrawal (PIX-03) ----------

/**
 * POST /carteira/sacar request. `chave` validation is intentionally loose —
 * each tipoChave has a different format (CPF=14 chars, email=any RFC5322,
 * celular=E.164, aleatoria=UUID). Backend re-validates per tipoChave inside
 * PagamentosService.
 */
export const SacarRequestSchema = z.object({
  chave: z.string().min(3).max(140),
  tipoChave: TipoChavePixSchema,
  /** Integer cents. Frontend converts BRL → cents before submit. */
  valorCents: z.number().int().positive(),
});

export const PixWithdrawalDtoSchema = z.object({
  id: z.string(),
  profissionalId: z.string().nullable().optional(),
  empresaId: z.string().nullable().optional(),
  chave: z.string(), // last-4-chars-masked on response (defense-in-depth; backend masks)
  tipoChave: TipoChavePixSchema,
  valorCents: z.string(), // BigInt as string
  status: WithdrawalStatusSchema,
  transferId: z.string().nullable(),
  motivoFalha: z.string().nullable(),
  criadoEm: z.string(),
  decididoEm: z.string().nullable(),
});

export const PixWithdrawalListResponseSchema = z.object({
  withdrawals: z.array(PixWithdrawalDtoSchema),
  total: z.number().int().nonnegative(),
});

// ---------- Phase 24 v1.6 — REQ-PIX-01 / D-08 Path 2 ----------

/**
 * Body for POST /carteira/sacar/com-chave-padrao. Only the valor is sent over
 * the wire — the server resolves the default chave from `Profissional.pixKeyCifrada`
 * to avoid exposing plaintext PII over the network. The existing per-saque
 * /carteira/sacar contract is preserved untouched (D-08).
 */
export const SacarComChavePadraoRequestSchema = z.object({
  /** Integer cents. Frontend converts BRL → cents before submit. */
  valorCents: z.number().int().positive(),
});

// ---------- Inferred types ----------

export type PagamentoStatus = z.infer<typeof PagamentoStatusSchema>;
export type TipoChavePix = z.infer<typeof TipoChavePixSchema>;
export type WithdrawalStatus = z.infer<typeof WithdrawalStatusSchema>;
export type PagamentoEventoDto = z.infer<typeof PagamentoEventoDtoSchema>;
export type SacarRequest = z.infer<typeof SacarRequestSchema>;
export type PixWithdrawalDto = z.infer<typeof PixWithdrawalDtoSchema>;
export type PixWithdrawalListResponse = z.infer<
  typeof PixWithdrawalListResponseSchema
>;
export type SacarComChavePadraoRequest = z.infer<
  typeof SacarComChavePadraoRequestSchema
>;
